import { Alert, Group, Loader, ScrollArea, Stack, Table, Text, Title } from '@mantine/core'
import { IconBrain } from '@tabler/icons-react'
import {
  filterOwlbertCloudLlmCatalogForHostedWeb,
  isOwlbertManagedForLlmsPolicy,
} from '@owlbert/core'
import React from 'react'
import { useOwlbertWebAuth } from '../../auth/OwlbertWebAuthContext'

type CatalogModel = {
  id: string
  provider: string
  model_id: string
  name: string
  supports_tools?: boolean
  context_window?: number
}

export const WebHostedLlmSettingsPanel: React.FC = () => {
  const auth = useOwlbertWebAuth()
  const [models, setModels] = React.useState<CatalogModel[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const managed = isOwlbertManagedForLlmsPolicy({
    hostedSessionActive: auth.sessionReady,
    isProMode: auth.isProMode,
    proDesktopBuild: false,
    owlbertManagedLlms: undefined,
  })

  React.useEffect(() => {
    if (!auth.sessionReady || !auth.bundle?.client) {
      setModels([])
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    void auth.bundle.client.llm.getModels
      .query()
      .then((res) => {
        if (cancelled) return
        const filtered = filterOwlbertCloudLlmCatalogForHostedWeb((res.models ?? []) as CatalogModel[])
        setModels(filtered)
      })
      .catch((e) => {
        if (cancelled) return
        setError(String((e as Error)?.message ?? e))
        setModels([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [auth.bundle?.client, auth.sessionReady])

  if (!auth.sessionReady) {
    return (
      <Stack gap="sm" maw={720}>
        <Text size="sm" c="dimmed">
          Sign in to see which chat models your account can use.
        </Text>
      </Stack>
    )
  }

  return (
    <Stack gap="md" maw={900}>
      <Group gap="sm" wrap="nowrap">
        <IconBrain size={28} />
        <div>
          <Title order={4}>Language models</Title>
          <Text size="sm" c="dimmed">
            The web app uses the hosted model catalog only (no custom providers or API keys here).
          </Text>
        </div>
      </Group>

      {managed ? (
        <Alert color="blue" title="Hosted models">
          Chat and assistants use models from the catalog your account can access. Pick a model when creating or editing
          an assistant on the Chat page or under Settings → Assistants.
        </Alert>
      ) : null}

      {loading ? (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading model catalog…
          </Text>
        </Group>
      ) : error ? (
        <Alert color="red" title="Could not load models">
          {error}
        </Alert>
      ) : (
        <ScrollArea mah={480} type="auto">
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Id</Table.Th>
                <Table.Th>Tools</Table.Th>
                <Table.Th>Context</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {models.map((m) => (
                <Table.Tr key={m.id}>
                  <Table.Td>{m.name}</Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {m.id}
                    </Text>
                  </Table.Td>
                  <Table.Td>{m.supports_tools ? 'Yes' : '—'}</Table.Td>
                  <Table.Td>{m.context_window != null ? String(m.context_window) : '—'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}
    </Stack>
  )
}
